<template>
  <hr-form-dialog
    ref="dialog"
    size="lg"
    :title="editClient.id
      ? $t('clients.modals.editClient.textEditClient')
      : $t('clients.modals.editClient.textAddClient')"
    data-e2e="dialog-edit-client"
    @submit="onSaveClient"
    @hide="onDialogHide"
  >
    <hr-tabs
      v-model="tab"
      class="col-12 q-mb-md"
    >
      <hr-tab
        name="profile"
        :label="$t('clients.modals.editClient.tabProfile')"
        data-e2e="tab-client-profile"
      />
      <hr-tab
        v-if="canEditClinical"
        name="clinical"
        :label="$t('clients.modals.editClient.tabClinical')"
        data-e2e="tab-client-clinical"
      />
      <hr-tab
        name="information"
        :label="$t('clients.modals.editClient.tabInformation')"
        data-e2e="tab-client-information"
      />
      <hr-tab
        v-if="canManageUsers"
        name="users"
        :label="$t('clients.modals.editClient.tabAccess')"
        data-e2e="tab-client-access"
      />
      <hr-tab
        v-if="canEditAvailability"
        name="availability"
        :label="$t('clients.modals.editClient.tabAvailability')"
      />
      <hr-tab
        v-if="canEditAuthorizations"
        name="authorizations"
        data-e2e="tab-client-services"
        :label="$t('clients.modals.editClient.tabServices')"
      />
      <hr-tab
        v-if="canEditCoverage"
        name="coverage"
        :label="$t('clients.modals.editClient.tabCoverage')"
        data-e2e="tab-client-coverage"
      />
      <!--
        The tab KEY is `billingAuthorizations` because `authorizations` is taken by the legacy
        scheduling tab above, whose visible label is "Treatment Services". The two read as different
        things because they are: that one governs what can be SCHEDULED, this one what can be BILLED.
      -->
      <hr-tab
        v-if="canEditBillingAuthorizations"
        name="billingAuthorizations"
        :label="$t('clients.modals.editClient.tabAuthorizations')"
        data-e2e="tab-client-billing-authorizations"
      />
    </hr-tabs>
    <hr-tab-panels
      v-model="tab"
      @before-transition="onSwitchTab"
    >
      <hr-tab-panel
        name="profile"
      >
        <q-card-section class="q-pt-none">
          <banner
            v-if="isIntegrationSourceOfTruth"
            :message="integrationSourceOfTruthText('integrationIsSourceOfTruth')"
            show-icon
            class="q-mb-lg"
          />
          <div class="row q-col-gutter-md">
            <div
              class="col-xs-12 col-md-3 col-lg-2 flex justify-center"
            >
              <q-avatar
                size="125px"
                class="q-mr-sm"
              >
                <q-img :src="clientPhoto" />
                <input
                  v-if="!offlineMode"
                  ref="file"
                  accept="image/*"
                  type="file"
                  class="file-overlay cursor-pointer"
                  @input="onSelectUploadedFile"
                >
                <div
                  v-if="!offlineMode"
                  class="file-overlay client-photo-overlay cursor-pointer"
                  @click="$refs.file.click()"
                >
                  <q-icon
                    name="photo_camera"
                    color="white"
                    size="lg"
                  />
                </div>

                <q-btn
                  :disable="offlineMode"
                  flat
                  round
                  size="12px"
                  icon="more_vert"
                  class="avatar-actions"
                  @click.stop
                >
                  <q-menu>
                    <q-list>
                      <q-item
                        v-ripple
                        v-close-popup
                        clickable
                        @click="$refs.file.click()"
                      >
                        <q-item-section avatar>
                          <q-icon name="photo_camera" />
                        </q-item-section>
                        <q-item-section>
                          {{ $t('clients.modals.editClient.btnSetNewPhoto') }}
                        </q-item-section>
                      </q-item>
                      <q-item
                        v-if="selectedClientPhoto || editClient.photo"
                        v-ripple
                        v-close-popup
                        clickable
                        @click="onPhotoDelete"
                      >
                        <q-item-section avatar>
                          <q-icon name="delete" />
                        </q-item-section>
                        <q-item-section>
                          {{ $t('common.buttonDelete') }}
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </q-menu>
                </q-btn>
              </q-avatar>
            </div>
            <div class="col-xs-12 col-md-9 col-lg-10">
              <div class="row q-col-gutter-md">
                <div
                  v-if="connectedClinic.integration && !offlineMode"
                  class="col-12"
                >
                  <q-btn
                    v-if="isIntegrationLinked"
                    icon="link"
                    color="primary"
                    outline
                    :disable="autoImportClients"
                    :label="$t('clients.modals.editClient.buttonEditIntegration', {
                      integration: getIntegrationName(connectedClinic.integration)
                    })"
                    class="full-width"
                    @click="onEditIntegration"
                  />
                  <q-btn
                    v-else
                    icon="link"
                    color="primary"
                    outline
                    :disable="autoImportClients"
                    :label="$t('clients.modals.editClient.buttonLinkIntegration', {
                      integration: getIntegrationName(connectedClinic.integration)
                    })"
                    class="full-width"
                    @click="onEditIntegration"
                  />
                </div>
                <component
                  :is="inputField.is"
                  v-for="inputField in inputFields.nameSection"
                  v-show="inputField.show"
                  :key="inputField.model"
                  v-bind="inputField.bindProperties"
                  v-model="inputField.parent[inputField.model]"
                >
                  <q-tooltip v-if="isIntegrationSourceOfTruth && inputField.appendImage">
                    {{ integrationSourceOfTruthText() }}
                  </q-tooltip>
                  <template #append>
                    <q-avatar
                      v-if="inputField.appendImage"
                      size="24px"
                    >
                      <img :src="inputField.appendImage">
                    </q-avatar>
                  </template>
                </component>
              </div>
            </div>
            <component
              :is="inputField.is"
              v-for="inputField in inputFields.informationSection"
              v-show="inputField.show"
              :key="inputField.model"
              v-bind="inputField.bindProperties"
              v-model="inputField.parent[inputField.model]"
            >
              <q-tooltip v-if="isIntegrationSourceOfTruth && inputField.appendImage">
                {{ integrationSourceOfTruthText() }}
              </q-tooltip>
              <template #append>
                <q-avatar
                  v-if="inputField.appendImage"
                  size="24px"
                >
                  <img :src="inputField.appendImage">
                </q-avatar>
              </template>
            </component>
            <q-select
              v-if="hasCareModelCohorts"
              v-model="clientCareModelCohort"
              :options="careModelCohortOptions"
              :label="$t('clients.modals.editClient.inputClientCohortLabel')"
              :display-value="selectedCareModelCohortLabel"
              :hint="careModelCohortHint"
              :hide-bottom-space="hideCareModelCohortBottomSpace"
              outlined
              clearable
              :disable="offlineMode"
              option-label="displayLabel"
              option-value="id"
              emit-value
              map-options
              class="col-12 cohort-select"
              @update:model-value="onChangeCareModelCohort"
            >
              <template #append>
                <q-chip
                  v-if="!isMobileScreen && isSelectedCareModelCohortSuggested"
                  dense
                  square
                  color="warning"
                  text-color="white"
                  class="q-mr-sm"
                >
                  {{ $t('cohorts.suggested') }}
                </q-chip>
              </template>

              <template #option="scope">
                <q-item v-bind="scope.itemProps">
                  <q-item-section>
                    <div class="row items-start justify-between">
                      <q-item-label class="cohort-option-label col">
                        {{ scope.opt.cohortName }}
                      </q-item-label>

                      <q-chip
                        v-if="!isMobileScreen && isSuggestedCareModelCohort(scope.opt.id)"
                        dense
                        square
                        color="warning"
                        text-color="white"
                        class="q-ml-sm q-mt-xs"
                      >
                        {{ $t('cohorts.suggested') }}
                      </q-chip>
                    </div>

                    <q-item-label caption>
                      {{ getCareModelCohortOptionCaption(scope.opt) }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-select
              v-if="hasUnits"
              v-model="selectedUnit"
              :options="unitsList"
              :label="$t('clients.modals.editClient.inputClientUnitLabel')"
              :disable="offlineMode"
              :readonly="isIntegrationManaged('units')"
              :hide-dropdown-icon="isIntegrationManaged('units')"
              clearable
              outlined
              emit-value
              map-options
              class="col-12"
              @update:model-value="onChangeSelectedUnit"
            >
              <q-tooltip v-if="isIntegrationManaged('units')">
                {{ integrationSourceOfTruthText() }}
              </q-tooltip>
              <template #append>
                <q-avatar
                  v-if="isIntegrationManaged('units')"
                  size="24px"
                >
                  <img :src="integrationLogo">
                </q-avatar>
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                >
                  <q-item-section>
                    <!--eslint-disable-next-line vue/no-v-text-v-html-on-component-->
                    <q-item-label v-html="scope.opt.label" />
                    <q-item-label caption>
                      {{ scope.opt.hint }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <div class="col-12">
              <q-btn-toggle
                :model-value="editClient.status"
                :disable="isStatusSwitchDisabled"
                :options="clientStatuses"
                spread
                data-e2e="toggle-client-status"
                @update:model-value="onChangeClientStatus"
              />
            </div>
            <div
              v-if="isSubscriptionUnavailable"
              class="col-12"
            >
              <banner
                type="error"
                :message="$t('clients.modals.editClient.noSubscription')"
              />
            </div>
            <div
              v-else-if="licenseNeeded"
              class="col-12"
            >
              <banner
                type="warning"
                :message="$t('clients.modals.editClient.licenseNeeded')"
              />
            </div>
          </div>
        </q-card-section>
      </hr-tab-panel>
      <hr-tab-panel
        v-if="canEditClinical"
        name="clinical"
      >
        <!-- DS-only inside this tab. Diagnoses are an array on the client doc, saved with the client. -->
        <div class="diagnoses">
          <hr-list>
            <hr-list-section-header>
              {{ $t('clients.modals.editClient.clinical.sectionActive') }}
            </hr-list-section-header>

            <!-- Active diagnoses are the priority-ordered set; primary = position 0. Reorder by
                 dragging the handle (immediate) or long-pressing the row body. When there are none,
                 the outlined add button below stands in as the empty state. -->
            <hr-sortable-list
              v-if="activeDiagnoses.length"
              :model-value="activeDiagnoses"
              item-key="id"
              long-press-body
              @update:model-value="onReorderActiveDiagnoses"
            >
              <template #item="{ element: diagnosis }">
                <hr-sortable-item
                  :label="diagnosis.code"
                  :caption="diagnosis.description || null"
                  clickable
                  segmented
                  data-e2e="list-item-diagnosis"
                  @click="onEditDiagnosis(diagnosis)"
                >
                  <template #trailing>
                    <hr-overflow-menu-btn
                      :options="diagnosisMenuOptions(diagnosis)"
                      @click.stop
                    />
                  </template>
                </hr-sortable-item>
              </template>
            </hr-sortable-list>

            <hr-list-add-btn
              :variant="activeDiagnoses.length ? 'flat' : 'outlined'"
              :label="$t('clients.modals.editClient.clinical.buttonAddDiagnosis')"
              data-e2e="button-add-diagnosis"
              @click="onAddDiagnosis"
            />

            <div
              v-if="resolvedDiagnoses.length"
              class="diagnoses-resolved"
            >
              <hr-list-section-header>
                {{ $t('clients.modals.editClient.clinical.resolved') }}
              </hr-list-section-header>
              <hr-list-item
                v-for="diagnosis in resolvedDiagnoses"
                :key="diagnosis.id"
                :label="diagnosis.code"
                :caption="diagnosis.description || null"
                clickable
                segmented
                muted
                data-e2e="list-item-diagnosis-resolved"
                @click="onEditDiagnosis(diagnosis)"
              >
                <template #trailing>
                  <hr-overflow-menu-btn
                    :options="diagnosisMenuOptions(diagnosis)"
                    @click.stop
                  />
                </template>
              </hr-list-item>
            </div>
          </hr-list>
        </div>
      </hr-tab-panel>
      <hr-tab-panel
        name="information"
      >
        <q-card-section class="row q-col-gutter-md">
          <info
            v-if="!hasClientDataFields"
            type="warning"
          >
            {{ $t('clients.modals.editClient.infoNoFields') }}
          </info>

          <q-item
            v-for="dataField in fieldsWithoutSection"
            :key="dataField.id"
            class="col-12"
          >
            <custom-data-field
              :model-value="getClientData(dataField)"
              :data-field="dataField"
              :client-id="editClient.id"
              @update:model-value="setClientData(dataField.id, $event)"
            />
          </q-item>
          <div
            v-for="(section, idx) in visibleSections"
            :key="section.id"
            class="col-12"
          >
            <q-expansion-item
              :label="section.label"
              class="text-weight-bold"
              switch-toggle-side
              expand-separator
              :default-opened="idx === 0"
              @show="onClientDataSectionShown(section)"
            >
              <q-item
                v-for="dataField in clientDataFields.filter(f => f.section === section.id)"
                :key="dataField.id"
                class="q-ml-lg"
              >
                <custom-data-field
                  :model-value="getClientData(dataField)"
                  :data-field="dataField"
                  :client-id="editClient.id"
                  @update:model-value="setClientData(dataField.id, $event)"
                />
              </q-item>
            </q-expansion-item>
          </div>
        </q-card-section>
      </hr-tab-panel>
      <hr-tab-panel
        v-if="canManageUsers"
        name="users"
      >
        <q-card-section class="q-pt-none">
          <div
            class="text-caption"
            v-html="infoText"
          />
        </q-card-section>
        <q-card-section>
          <q-table
            v-model:selected="selectedUsers"
            title=""
            flat
            bordered
            :columns="userColumns"
            :pagination="usersPagination"
            :rows="availableUsers"
            :filter="usersFilter"
            :loading="loadingUsers || isWithdrawUserInvitationLoading"
            row-key="id"
            :selection="offlineMode ? 'none' : 'multiple'"
            :no-data-label="$t('clients.modals.editClient.userTableNoData')"
            :no-results-label="$t('clients.modals.editClient.userTableNoResults')"
            virtual-scroll
            :virtual-scroll-item-size="48"
            :virtual-scroll-sticky-size-start="48"
            :table-style="{ maxHeight: '60vh' }"
          >
            <template #top>
              <q-btn-dropdown
                v-if="isManagerOrAdministrator"
                ref="rolesDropdown"
                color="primary"
                :label="$t('settings.users.buttonInviteUser')"
                :disable="offlineMode"
              >
                <q-list>
                  <q-item
                    v-for="userRole in userRoleList"
                    :key="userRole.value"
                    v-close-popup
                    clickable
                    @click="onInviteUser(userRole)"
                  >
                    <q-item-section>
                      <q-item-label>{{ userRole.label }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>

              <q-btn-dropdown
                v-else
                ref="rolesDropdown"
                color="primary"
                :label="$t('settings.users.buttonInviteUser')"
                :disable="offlineMode || !client?.id"
              >
                <q-list>
                  <q-item
                    v-for="userRole in userRoleList"
                    :key="userRole.value"
                    v-close-popup
                    clickable
                    @click="onInviteUser(userRole)"
                  >
                    <q-item-section>
                      <q-item-label>{{ userRole.label }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
              <q-tooltip
                v-if="!isManagerOrAdministrator && !client?.id"
                anchor="center right"
                self="center left"
                :offset="[10, 10]"
              >
                {{ $t('clients.modals.manageTrainers.buttonInviteParentTooltip') }}
              </q-tooltip>
              <q-space />

              <search-input
                v-model="usersFilter"
                input-class="mobile-search"
              />
            </template>
            <template #body-cell-name="props">
              <q-td
                :props="props"
                :data-e2e="`row-user-${props.row.email}`"
              >
                <q-icon
                  v-if="isInvitedUser(props)"
                  name="email"
                  size="sm"
                  color="warning"
                >
                  <q-tooltip v-if="isInvitedUser(props)">
                    {{ $t('clients.modals.manageTrainers.tooltipInvitationNotAccepted') }}
                  </q-tooltip>
                </q-icon>
                <span v-else>{{ props.value }}</span>
              </q-td>
            </template>
            <template #body-cell-email="props">
              <q-td
                :props="props"
              >
                {{ props.value }}
              </q-td>
            </template>
            <template #body-selection="props">
              <div v-if="isInvitedUser(props)">
                <q-btn
                  dense
                  round
                  flat
                  color="dark-variant"
                  icon="cancel"
                  :disable="isWithdrawUserInvitationLoading"
                  @click="onWithdrawUserInvitation(props.row)"
                />
                <q-tooltip>
                  {{ $t('clients.modals.manageTrainers.tooltipInvitationWithdraw') }}
                </q-tooltip>
              </div>
              <q-checkbox
                v-else
                v-model="selectedUsers"
                data-e2e="checkbox-select-user"
                :val="props.row"
                :disable="isUserBoundWithUnit(props.row) || isUserManagerOrAdministrator(props.row)"
              >
                <q-tooltip v-if="isUserManagerOrAdministrator(props.row)">
                  {{ $t('clients.modals.editClient.userIsManagerOrAdministrator') }}
                </q-tooltip>
                <q-tooltip v-else-if="isUserBoundWithUnit(props.row)">
                  {{ $t('clients.modals.editClient.userBoundThroughUnit') }}
                </q-tooltip>
              </q-checkbox>
            </template>
          </q-table>
        </q-card-section>
      </hr-tab-panel>
      <hr-tab-panel
        name="availability"
      >
        <availability-editor
          ref="availabilityEditor"
          v-model="editClientAvailability"
        />
      </hr-tab-panel>
      <hr-tab-panel
        name="authorizations"
      >
        <div class="row q-col-gutter-md">
          <div class="col-12">
            <authorized-services-table
              :authorizations="filteredAuthorizations"
              :loading="isLoadingAuthorizations"
              hide-filter
              @create-authorizations="onCreateAuthorizations"
              @update-authorization="onUpdateAuthorization"
              @delete-authorization="onDeleteAuthorization"
            />
          </div>
          <div class="col-12">
            <treatment-services-table
              :rows="formattedValidTreatmentServices"
              :loading="isAppointmentsLoading"
              :rows-per-page="10"
              :no-data-label="$t('authorizations.unauthorizedNoData')"
              data-e2e="table-unauthorized-services"
              show-delete
              flat
              bordered
              @delete="onRemoveValidTreatmentService"
            >
              <template #top>
                <div class="col">
                  <div class="text-h6">
                    {{ $t('authorizations.unauthorizedServices') }}
                  </div>
                  <div class="q-mr-md text-caption">
                    {{ $t('authorizations.captionUnauthorizedServices') }}
                  </div>
                </div>
                <div class="col-auto">
                  <treatment-services-selector
                    v-model="editValidTreatmentServices"
                    :available-treatment-services="availableTreatmentServices"
                    hide-items
                    :label="$t('common.buttonAdd')"
                    class="col-12"
                  />
                </div>
              </template>
            </treatment-services-table>
          </div>
        </div>
      </hr-tab-panel>
      <hr-tab-panel
        v-if="canEditCoverage"
        name="coverage"
      >
        <client-coverage-panel
          :coverages="clientCoverages"
          :client="editClient"
          :client-id="client.id || null"
          :clinic-id="client.clinic || userDetails.clinic"
          :clinic-country="connectedClinic.country"
          :readonly="!canManageCoverage"
          @error="onRevenueCycleError"
          @update:client="onCoveragePolicyholderEdited"
        />
      </hr-tab-panel>
      <hr-tab-panel
        v-if="canEditBillingAuthorizations"
        name="billingAuthorizations"
      >
        <client-authorization-panel
          :authorizations="clientAuthorizations"
          :client-id="client.id || null"
          :clinic-id="client.clinic || userDetails.clinic"
          :treatment-services="treatmentServices"
          :places-of-service="placesOfService"
          :places-of-service-unavailable="placesOfServiceUnavailable"
          @error="onRevenueCycleError"
        />
      </hr-tab-panel>
    </hr-tab-panels>
    <q-dialog
      v-model="showInvite"
      class="row col-6"
    >
      <invite-trainer
        :invitation="invitation"
        @send-invitation="sendInvitationMail"
      />
    </q-dialog>

    <template #destructive-actions>
      <div class="q-gutter-sm row items-center wrap">
        <q-field
          v-if="userIsAdmin && client.id"
          outlined
          dense
          readonly
        >
          <template #control>
            <div
              class="self-center full-width no-outline cursor-pointer text-grey-6"
              @click="onCopyClientId"
            >
              {{ $t('clients.modals.editClient.copyClientId') }}
            </div>
          </template>
          <template #append>
            <q-icon
              name="content_copy"
              class="cursor-pointer q-ml-xs text-grey-6"
              @click="onCopyClientId"
            />
          </template>
          <q-tooltip>{{ $t('clients.modals.editClient.copyClientId') }}</q-tooltip>
        </q-field>
        <q-field
          outlined
          dense
          readonly
        >
          <template #control>
            <div
              class="self-center full-width no-outline cursor-pointer text-grey-6"
              @click="onCopySupportInfo"
            >
              {{ $t('common.copySupportInfo') }}
            </div>
          </template>
          <template #append>
            <q-icon
              name="content_copy"
              class="cursor-pointer q-ml-xs text-grey-6"
              @click="onCopySupportInfo"
            />
          </template>
          <q-tooltip>{{ $t('common.copySupportInfo') }}</q-tooltip>
        </q-field>
      </div>
    </template>

    <template #actions>
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        @click="onCancelClick"
      />
      <hr-confirm-btn
        type="submit"
        :disable="!networkOnline || isSubscriptionUnavailable || offlineMode"
        :loading="waitingSpinner"
        :label="$t('common.buttonSave')"
        data-e2e="button-save-client"
      />
    </template>
  </hr-form-dialog>
</template>
<script>
import { Clipboard } from '@capacitor/clipboard';
import { countries } from 'boot/countries';
import { callFirebaseFunction, storage } from 'boot/firebase';
import AuthorizedServicesTable from 'components/Billing/AuthorizedServicesTable';
import ServiceAppointmentConflictDialog from 'components/Billing/ServiceAppointmentConflictDialog.vue';
import TreatmentServicesTable from 'components/Billing/TreatmentServicesTable';
import InviteTrainer from 'components/Clients/Modals/InviteTrainer';
import DateInput from 'components/DateInput';
import Info from 'components/Info';
import EditIntegration from 'components/Integrations/EditIntegration';
import AvailabilityEditor from 'components/UI/AvailabilityEditor';
import Banner from 'components/UI/Banner';
import CountrySelect from 'components/UI/CountrySelect';
import SearchInput from 'components/UI/SearchInput';
import { Timestamp } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref } from 'firebase/storage';
import { chain, find, get, head, includes, isEmpty, isEqual, map, omit, trim, uniqBy } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { copyToClipboard, uid } from 'quasar';
import TreatmentServicesSelector from 'src/components/Billing/TreatmentServicesSelector.vue';
import DeleteDisableClient from 'src/components/Clients/Modals/DeleteDisableClient.vue';
import CustomDataField from 'src/components/DataFields/CustomDataField.vue';
import { useClients } from 'src/composables/clients';
import { useTime } from 'src/composables/time';
import { useTriggerNotifications } from 'src/composables/triggerNotifications';
import {
  CLIENT_STATUS,
  EDIT_CLIENT_PROFILE_TABS,
  EXCLUDE_CLIENT_PROFILE_FIELDS_SYNC,
  INTEGRATIONS_CLIENT_PROFILE_FIELDS,
} from 'src/constants/clients';
import {
  HUMANIZED_INTEGRATION_NAMES,
  INTEGRATIONS_LOGOS,
  POLLING_DRIVEN_INTEGRATIONS,
  V2_DRIVEN_INTEGRATIONS,
  INTEGRATION_NAMES,
} from 'src/constants/integrations';
import { CLIENTS_NOTIFICATIONS_TYPES } from 'src/constants/notifications';
import {
  ClientAuthorizationPanel,
  ClientCoveragePanel,
  useClientAuthorizations,
  useClientCoverages,
} from 'src/contexts/revenueCycle';
import { useCareModelCohortSuggestion } from 'src/domains/careModel';
import AppointmentsDB from 'src/fb/appointments-db';
import ClientAuthorizationsDB from 'src/fb/clientAuthorizations-db';
import Auth from 'src/mixins/Auth';
import Countries from 'src/mixins/Countries';
import EditClientUsersMixin from 'src/mixins/EditClientUsersMixin';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import LicenseProducts from 'src/mixins/LicenseProducts';
import Toolbox from 'src/mixins/Toolbox';
import { editClientProfileAnalytics } from 'src/services/analytics';
import { auth as authStore } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { dataFields } from 'src/store/dataFields';
import { firebaseUploader } from 'src/store/firebaseUploader';
import { network } from 'src/store/network';
import { units } from 'src/store/units';
import { users } from 'src/store/users';
import { copySupportInfoToClipboard, getClientInitials, notifyErrorWithDetails } from 'src/utils/AppUtils';
import { getIntegrationManagedFields } from 'src/utils/Clients';
import { calculateAge } from 'src/utils/DateUtils';
import { sendEventToAloha, updateAppointmentClientReference } from 'src/utils/Integrations';
import { logEvent } from 'src/utils/LogEvent';
import { EDIT_CLIENT_PROFILE_SOURCES } from 'src/constants/analytics';

import EditDiagnosisDialog from 'src/components/Clients/Modals/EditDiagnosisDialog.vue';
import {
  confirmDestructiveDialog,
  HrConfirmBtn, HrDismissBtn, HrFormDialog, HrList, HrListAddBtn, HrListItem, HrListSectionHeader,
  HrOverflowMenuBtn, HrSortableItem, HrSortableList,
  HrTab, HrTabPanel, HrTabPanels, HrTabs,
} from 'src/design-system';

export default {
  name: 'EditClient',

  components: {
    AvailabilityEditor,
    AuthorizedServicesTable,
    ClientAuthorizationPanel,
    ClientCoveragePanel,
    CountrySelect,
    DateInput,
    Info,
    SearchInput,
    TreatmentServicesTable,
    TreatmentServicesSelector,
    InviteTrainer,
    CustomDataField,
    Banner,
    HrConfirmBtn,
    HrDismissBtn,
    HrFormDialog,
    HrList,
    HrListAddBtn,
    HrListItem,
    HrListSectionHeader,
    HrOverflowMenuBtn,
    HrSortableItem,
    HrSortableList,
    HrTab,
    HrTabPanel,
    HrTabPanels,
    HrTabs,
  },

  mixins: [
    Auth,
    Countries,
    Integrations,
    Interactions,
    Toolbox,
    EditClientUsersMixin,
    LicenseProducts,
  ],

  props: {
    client: {
      type: Object,
      required: true,
    },
    associatedUsers: {
      type: Array,
      required: true,
    },
    cachedUserPrivileges: {
      type: Object,
      default: () => ({}),
    },
    tabToOpen: {
      type: String,
      default: null,
      validator: (value) => value === null || Object.values(EDIT_CLIENT_PROFILE_TABS).includes(value),
    },

    // Entry point for analytics tagging (e.g. 'SCHEDULING').
    source: {
      type: String,
      default: EDIT_CLIENT_PROFILE_SOURCES.UNKNOWN,
      validator: (value) => Object.values(EDIT_CLIENT_PROFILE_SOURCES).includes(value),
    },
  },

  emits: ['hide', 'ok', 'appointments-changed'],

  setup() {
    const { createClientsTriggerNotifications } = useTriggerNotifications();
    const { getClientProfileImagePath, getRandomColor, createNewStaffMember, getUserClientAssociations } = useClients();
    const { formatDate, adjustDate, addToDate } = useTime();
    const { getSuggestedCohortForClient } = useCareModelCohortSuggestion();
    // Owned here rather than inside the tab: tab panels unmount on switch, and staged coverage
    // edits must survive that and be flushed after the client is saved.
    const clientCoverages = useClientCoverages();
    // Same reason as coverage: a tab panel unmounts on switch, so staged authorization edits have to
    // outlive that, and the flush happens after the client is saved.
    const clientAuthorizations = useClientAuthorizations();

    return {
      clientAuthorizations,
      clientCoverages,
      createClientsTriggerNotifications,
      getClientProfileImagePath,
      getClientInitials,
      getRandomColor,
      createNewStaffMember,
      getUserClientAssociations,
      formatDate,
      adjustDate,
      addToDate,
      getSuggestedCohortForClient,
    };
  },

  data() {
    return {
      tab: EDIT_CLIENT_PROFILE_TABS.PROFILE,
      // Set before onSaveClient() redirects tab, so the watcher can skip tracking it.
      isTabChangeFromValidation: false,

      waitingSpinner: false,
      isLoadingAuthorizations: false,
      isAuthorizationsLoaded: false,
      placesOfServiceUnavailable: false,

      genderList: map(this.$tm('clients.modals.editClient.gender'), (label, value) => ({ value, label })),

      editClient: {
        clientName: '',
        customData: {},
        status: '',
        availability: [],
      },

      editAuthorizations: [],
      isAppointmentsLoading: false,

      integrationLinkEdited: false,
      selectedClientPhoto: null,
      deleteClientPhoto: false,

      selectedUnit: null,

      suggestedCareModelCohortId: null,
      isCareModelCohortManuallyChanged: false,
    };
  },

  computed: {
    ...mapState(authStore, [
      'userDetails',
      'connectedClinic',
      'billing',
      'currentClient',
      'treatmentServices',
      'placesOfService',
      'careModelCohorts',
      'userIsAdmin',
      'isManagerOrAdministrator',
      'hasSupervisorCredentials',
      'isSchedulingAvailable',
      'isRevenueCycleAvailable',
      'isBillingConfigAvailable',
      'userPrivilegesList',
      'isSubscriptionIncomplete',
      'isSubscriptionInactive',
      'integrationSettings',
      'autoImportClients',
    ]),
    ...mapState(network, ['networkOnline', 'offlineMode']),
    ...mapState(units, ['units', 'unitsList']),
    ...mapState(users, ['users']),
    ...mapState(dataFields, ['clientDataFields', 'clientDataFieldSections', 'clientSchedulingGroupsSection']),

    canEditAvailability() {
      return this.isSchedulingAvailable;
    }, // canEditAvailability()

    canEditAuthorizations() {
      return this.isSchedulingAvailable;
      // || this.userHasPrivilege('clients/editClientAuthorizations'); @todo Probably need a privilege
    }, // canEditAuthorizations()

    canEditCoverage() {
      return this.isRevenueCycleAvailable;
    }, // canEditCoverage()

    // Separate from canEditCoverage on purpose: seeing who pays is part of reading a client, so a
    // senior therapist gets the tab. Entering and reordering payers redirects money, so it stops at
    // supervisor. Mirrors the clientCoverages/billingIntents write guards, which are what enforce it.
    canManageCoverage() {
      return this.hasSupervisorCredentials;
    }, // canManageCoverage()

    // The BILLING authorizations tab, gated on the same add-on as coverage. The legacy
    // "Treatment Services" tab above rides `isSchedulingAvailable` instead, which is what keeps the
    // three entitlement segments apart without a mechanism of their own: billing-only clinics see
    // this one, scheduling-only clinics see that one, and clinics holding both see both.
    canEditBillingAuthorizations() {
      return this.isRevenueCycleAvailable;
    }, // canEditBillingAuthorizations()

    canEditClinical() {
      // Diagnoses are a billing projector input, and this tab is the only place to enter them —
      // so a clinic on the billing add-on needs it with no scheduling at all (HR-4800). This used
      // to read `isSchedulingAvailable` on the premise that billing sat downstream of scheduling.
      return this.isBillingConfigAvailable;
    }, // canEditClinical()

    activeDiagnoses() {
      // The priority-ordered set: sorted by `order`, so index 0 is primary.
      return (this.editClient.diagnoses || [])
        .filter((diagnosis) => !diagnosis.endDate)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }, // activeDiagnoses()

    resolvedDiagnoses() {
      // Resolved diagnoses aren't part of the priority order (no primary, not reorderable, `order`
      // is always null), so they're shown most-recently-resolved first.
      const toMillis = (ts) => (ts instanceof Timestamp ? ts.toMillis() : new Date(ts).getTime());
      return (this.editClient.diagnoses || [])
        .filter((diagnosis) => diagnosis.endDate)
        .sort((a, b) => toMillis(b.endDate) - toMillis(a.endDate));
    }, // resolvedDiagnoses()

    hasUnits() {
      return !isEmpty(this.units);
    }, // hasUnits()

    hasCareModelCohorts() {
      return !isEmpty(this.careModelCohorts);
    }, // hasCareModelCohorts()

    hasClientDataFields() {
      return !isEmpty(this.clientDataFields);
    }, // hasClientDataFields()

    isIntegrationLinked() {
      return get(this.editClient, 'integrationReference', null) !== null;
    },

    isSubscriptionUnavailable() {
      return this.isSubscriptionIncomplete
        || this.isSubscriptionInactive
        || this.billing.status === 'created';
    },

    canManageUsers() {
      return this.userHasPrivilege('users/manageUsers');
    },

    licenseNeeded() {
      if ((get(this.editClient, 'status') !== CLIENT_STATUS.ACTIVE)
        || ((this.client.id) && (this.client.status === CLIENT_STATUS.ACTIVE))) {
        return false;
      }

      const updatedUsage = get(this.billing, ['subscriptionLicenses', 'client', 'currentUsage'], 0) + 1;
      const quantity = get(this.billing, ['subscriptionLicenses', 'client', 'quantity'], 0);

      return updatedUsage > quantity;
    },

    isTrialing() {
      return ['trialing'].includes(this.billing.status);
    }, // isTrialing()

    clientStatuses() {
      return map([CLIENT_STATUS.INACTIVE, CLIENT_STATUS.ACTIVE], (status) => (
        {
          value: status,
          label: get(this.$tm('clients.status'), status),
        }
      ));
    }, // clientStatuses()

    clientPhoto() {
      if (this.selectedClientPhoto) {
        return URL.createObjectURL(this.selectedClientPhoto);
      }

      return this.getClientProfileImagePath(this.editClient, true);
    },

    clientFirstName: {
      get() {
        return this.editClient.firstName;
      },
      set(firstName) {
        const fullName = this.getFullName(this.editClient.firstName, this.editClient.lastName);
        const existingClientName = trim(this.editClient.clientName);
        if (!existingClientName || isEqual(fullName, existingClientName)) {
          this.editClient.clientName = this.getFullName(firstName, this.editClient.lastName);
        }
        this.editClient.firstName = firstName;
      },
    },

    clientLastName: {
      get() {
        return this.editClient.lastName;
      },
      set(lastName) {
        const fullName = this.getFullName(this.editClient.firstName, this.editClient.lastName);
        const existingClientName = trim(this.editClient.clientName);
        if (!existingClientName || isEqual(fullName, existingClientName)) {
          this.editClient.clientName = this.getFullName(this.editClient.firstName, lastName);
        }
        this.editClient.lastName = lastName;
      },
    },

    clientAge() {
      return calculateAge(this.clientBirthDate);
    }, // clientAge()

    clientBirthDate: {
      get() {
        return get(this.editClient, 'birthDate', null);
      },
      set(v) {
        // We set birthDate to 12:00 PM to avoid timezone issues
        this.editClient.birthDate = Timestamp.fromDate(this.adjustDate(v.toDate(), { hour: 12 }));
      },
    }, // clientBirthDate()

    clientCareModelCohort: {
      get() {
        return get(this.editClient, 'careModelCohort', null);
      },
      set(value) {
        this.editClient.careModelCohort = value;
      },
    },

    editClientAvailability: {
      get() {
        return get(this.editClient, 'availability', null);
      },
      set(value) {
        this.editClient.availability = value;
      },
    },

    availableTreatmentServices() {
      return this.treatmentServices.filter((service) => !service.allowUnauthorizedScheduling);
    },

    formattedValidTreatmentServices() {
      const clientTreatmentServices = this.editValidTreatmentServices.map((service) => {
        const treatmentService = find(this.availableTreatmentServices, { id: service?.id });

        if (!treatmentService) {
          return null;
        }

        return {
          ...treatmentService,
          ...service,
        };
      }).filter(Boolean);

      const allowUnauthorizedServices = this.treatmentServices.filter((service) => service.allowUnauthorizedScheduling);

      return [...clientTreatmentServices, ...allowUnauthorizedServices];
    }, // formattedValidTreatmentServices()

    editValidTreatmentServices: {
      get() {
        return get(this.editClient, 'validTreatmentServices', []);
      },
      set(value) {
        this.editClient.validTreatmentServices = value;
      },
    },

    careModelCohortOptions() {
      return this.careModelCohorts.map((cohort) => ({
        ...cohort,
        displayLabel: cohort.cohortName,
      }));
    },

    selectedCareModelCohortLabel() {
      if (!this.clientCareModelCohort) {
        return this.$t('cohorts.unassignedLabel');
      }

      const selectedOption = this.careModelCohortOptions.find((option) => option.id === this.clientCareModelCohort);

      return selectedOption?.cohortName || this.$t('cohorts.unassignedLabel');
    },

    isMobileScreen() {
      return this.$q.screen.lt.sm;
    },

    isSelectedCareModelCohortSuggested() {
      return this.isSuggestedCareModelCohort(this.clientCareModelCohort);
    },

    careModelCohortHint() {
      return this.isMobileScreen && this.isSelectedCareModelCohortSuggested
        ? this.$t('cohorts.suggested')
        : '';
    },

    hideCareModelCohortBottomSpace() {
      return !(this.isMobileScreen && this.isSelectedCareModelCohortSuggested);
    },

    fieldsWithoutSection() {
      const availableSections = new Set(this.clientDataFieldSections.map(({ id }) => id));

      return this.clientDataFields.filter((field) => {
        if (!field.section) {
          return true;
        }

        return !availableSections.has(field.section);
      });
    }, // fieldsWithoutSection()

    visibleSections() {
      return this.clientDataFieldSections.filter((section) => {
        return section.isDefaultSection ? section.isVisible : true;
      });
    }, // visibleSections()

    userRoleList() {
      const userRoleLevel = chain(this.userPrivilegesList)
        .find({ value: this.userDetails.userPrivilege || this.userDetails.role })
        .get('level', 1000)
        .value();

      return this.userPrivilegesList.reduce((acc, userPrivileges) => {
        if (userPrivileges.level < userRoleLevel) {
          return acc;
        }

        acc.push({
          ...userPrivileges,
          disable: userPrivileges.level < userRoleLevel,
        });

        return acc;
      }, []);
    },

    filteredAuthorizations() {
      return this.editAuthorizations.filter((auth) => (auth.editStatus !== 'delete'));
    }, // filteredAuthorizations()

    isIntegrationSourceOfTruth() {
      return this.editClient?.integrationReference && this.integrationSettings.isIntegrationSourceOfTruth;
    },

    isStatusSwitchDisabled() {
      return this.offlineMode || (this.editClient?.integrationReference && this.autoImportClients);
    },

    integrationLogo() {
      return INTEGRATIONS_LOGOS[this.connectedClinic.integration];
    },

    inputFields() {
      const defaultProperties = (fieldName = '') => ({
        show: true,
        appendImage: this.isIntegrationManaged(fieldName) ? this.integrationLogo : '',
        is: 'q-input',
        parent: this.editClient,
      });

      const defaultBindProperties = (fieldName = '') => ({
        readonly: this.isIntegrationManaged(fieldName),
        disable: this.offlineMode,
        type: 'text',
        outlined: true,
      });

      const hasPostalCode = this.countryHasPostalCode(this.editClient?.country);

      return {
        nameSection: [
          {
            model: 'clientFirstName',
            ...defaultProperties('firstName'),
            parent: this,
            bindProperties: {
              ...defaultBindProperties('firstName'),
              autofocus: true,
              outlined: this.$q.screen.lt.md,
              'hide-bottom-space': true,
              label: this.$t('clients.modals.editClient.inputClientFirstNameLabel'),
              class: 'col-xs-12 col-sm-6',
              'data-e2e': 'input-first-name-client',
            },
          },
          {
            model: 'clientLastName',
            ...defaultProperties('lastName'),
            parent: this,
            bindProperties: {
              ...defaultBindProperties('lastName'),
              outlined: this.$q.screen.lt.md,
              'hide-bottom-space': true,
              label: this.$t('clients.modals.editClient.inputClientLastNameLabel'),
              class: 'col-xs-12 col-sm-6',
              'data-e2e': 'input-last-name-client',
            },
          },
          {
            model: 'clientName',
            ...defaultProperties(),
            bindProperties: {
              ...defaultBindProperties(),
              ref: 'clientName',
              'data-e2e': 'input-preferred-name-client',
              outlined: this.$q.screen.lt.md,
              'hide-bottom-space': true,
              label: this.$t('clients.modals.editClient.inputClientNameLabel'),
              class: 'col-xs-12 col-sm-6 col-md-8',
              rules: [(val) => !!val || this.$t('clients.modals.editClient.inputValidatorEnterPreferredName')],
            },
          },
          {
            model: 'alias',
            ...defaultProperties(),
            bindProperties: {
              ...defaultBindProperties(),
              outlined: this.$q.screen.lt.md,
              'hide-bottom-space': true,
              label: this.$t('clients.modals.editClient.inputClientAliasLabel'),
              class: 'col-xs-12 col-sm-6 col-md-4',
              'data-e2e': 'input-alias-client',
            },
          },
        ],
        informationSection: [
          {
            model: 'country',
            ...defaultProperties('country'),
            is: 'country-select',
            bindProperties: {
              ...defaultBindProperties('country'),
              rules: [(val) => !!val || this.$t('clients.modals.editClient.inputClientCountryValidator')],
              label: this.$t('clients.modals.editClient.inputClientCountryLabel'),
              'emit-value': true,
              'map-options': true,
              'show-flag': true,
              ...(!this.isIntegrationSourceOfTruth && { hint: this.$t('common.requiredField') }),
              'hide-bottom-space': this.isIntegrationSourceOfTruth,
              class: { 'col-sm-8': this.countryHasStates(this.editClient.country), 'col-xs-12': true },
              'hide-dropdown-icon': this.isIntegrationSourceOfTruth,
              appendImage: defaultProperties('country').appendImage,
              'data-e2e': 'dropdown-country-of-client',
            },
          },
          {
            model: 'state',
            ...defaultProperties('state'),
            is: 'q-select',
            show: this.countryHasStates(this.editClient?.country),
            bindProperties: {
              ...defaultBindProperties('state'),
              label: this.countryStateLabel(this.editClient?.country),
              class: 'col-xs-12 col-sm-4',
              options: this.countryStates(this.editClient?.country),
              'option-value': 'code',
              'emit-value': true,
              'map-options': true,
              'hide-bottom-space': true,
              'hide-dropdown-icon': this.isIntegrationSourceOfTruth,
              'data-e2e': 'dropdown-state-of-client',
            },
          },
          {
            model: 'address1',
            ...defaultProperties('address1'),
            bindProperties: {
              ...defaultBindProperties('address1'),
              class: 'col-12',
              label: this.$t('clients.modals.editClient.inputClientAddress1Label'),
              'data-e2e': 'input-address-client',
            },
          },
          {
            model: 'address2',
            ...defaultProperties('address2'),
            bindProperties: {
              ...defaultBindProperties('address2'),
              class: 'col-12',
              label: this.$t('clients.modals.editClient.inputClientAddress2Label'),
              'data-e2e': 'input-address2-client',
            },
          },
          {
            model: 'postalCode',
            ...defaultProperties('postalCode'),
            show: hasPostalCode,
            bindProperties: {
              ...defaultBindProperties('postalCode'),
              class: 'col-xs-12 col-sm-4',
              label: this.countryPostalCodeLabel(this.editClient?.country),
              'data-e2e': 'input-code-postal-client',
            },
          },
          {
            model: 'city',
            ...defaultProperties('city'),
            bindProperties: {
              ...defaultBindProperties('city'),
              class: `col-xs-12  ${hasPostalCode ? 'col-sm-8' : ''}`,
              label: this.$t('clients.modals.editClient.inputClientCityLabel'),
              'data-e2e': 'input-city-client',
            },
          },
          {
            model: 'phone',
            ...defaultProperties('phone'),
            bindProperties: {
              ...defaultBindProperties('phone'),
              class: `col-xs-12  ${hasPostalCode ? 'col-sm-6' : ''}`,
              label: this.$t('clients.modals.editClient.inputClientPhoneLabel'),
              'data-e2e': 'input-phone-client',
            },
          },
          {
            model: 'email',
            ...defaultProperties('email'),
            bindProperties: {
              ...defaultBindProperties('email'),
              class: 'col-xs-12 col-sm-6',
              label: this.$t('clients.modals.editClient.inputClientEmailLabel'),
              type: 'email',
              'data-e2e': 'input-email-client',
            },
          },
          {
            model: 'gender',
            options: this.genderList,
            ...defaultProperties('gender'),
            is: 'q-select',
            bindProperties: {
              ...defaultBindProperties('gender'),
              class: 'col-xs-12 col-sm-6',
              label: this.$t('clients.modals.editClient.inputClientGenderLabel'),
              'emit-value': true,
              'map-options': true,
              options: this.genderList,
              clearable: true,
              'hide-dropdown-icon': this.isIntegrationSourceOfTruth,
              'data-e2e': 'dropdown-gender-client',
            },
          },
          {
            model: 'reference',
            ...defaultProperties('reference'),
            bindProperties: {
              ...defaultBindProperties('reference'),
              label: this.$t('clients.modals.editClient.inputClientReferenceIDLabel'),
              class: 'col-xs-12 col-sm-6',
              'data-e2e': 'input-reference-id',
            },
          },
          {
            model: 'clientBirthDate',
            ...defaultProperties('birthDate'),
            parent: this,
            is: 'date-input',
            bindProperties: {
              ...defaultBindProperties('birthDate'),
              options: this.maxBirthDate,
              class: 'col-6',
              label: this.$t('clients.modals.editClient.inputClientDOBLabel'),
              appendImage: defaultProperties('birthDate').appendImage,
              'data-e2e': 'date-selector-birthday-client',
            },
          },
          {
            model: 'clientAge',
            ...defaultProperties('birthDate'),
            parent: this,
            bindProperties: {
              ...defaultBindProperties('birthDate'),
              readonly: true,
              class: 'col-6',
              label: this.$t('clients.modals.editClient.inputClientAgeLabel'),
            },
          },
          {
            model: 'notes',
            ...defaultProperties(),
            bindProperties: {
              ...defaultBindProperties(),
              autogrow: true,
              class: 'col-12',
              type: 'textarea',
              label: this.$t('clients.modals.editClient.inputClientNotesLabel'),
              'data-e2e': 'input-notes-client',
            },
          },
        ],
      };
    },
  },

  watch: {
    client: {
      immediate: true,
      deep: true,
      handler(client) {
        const avatarBgColor = !client.avatarBgColor
          ? this.getRandomColor()
          : client.avatarBgColor;

        this.editClient = {
          ...client,
          type: 'client',
          avatarBgColor,
        };

        this.suggestedCareModelCohortId = null;
        this.isCareModelCohortManuallyChanged = false;
      },
    },

    associatedUsers: {
      immediate: true,
      deep: true,
      handler(to) {
        this.clientUsers = [...to];
      },
    },

    editClient: {
      deep: true,
      handler() {
        this.updateSuggestedCareModelCohort();
      },
    },

    tab(newTab, oldTab) {
      if (this.isTabChangeFromValidation) {
        this.isTabChangeFromValidation = false;
        return;
      }

      editClientProfileAnalytics.trackProfileTabSwitched({
        targetClientId: this.client.id, source: this.source, fromTab: oldTab, toTab: newTab,
      });
    }, // tab
  }, // watch

  async created() {
    // Lazily load treatment services (no longer eager-loaded at login).
    // Fire-and-forget: ensureTreatmentServicesBound rejects on a bind error (which it
    // already logs internally), so swallow here to avoid an unhandled rejection.
    this.ensureTreatmentServicesBound().catch(() => {});
    // Places of service bind lazily too, and the authorizations tab needs them for a limit's
    // setting scope. The failure is otherwise silent, because an unbound store property is an
    // empty array — indistinguishable from a clinic that configured none, while an empty setting
    // scope MEANS "any setting" and would be saved as a wider approval than the letter granted.
    // Recorded rather than rethrown: the tab still works, and the picker says why it is empty.
    if (this.canEditBillingAuthorizations) {
      this.ensurePlacesOfServiceBound().catch(() => {
        this.placesOfServiceUnavailable = true;
      });
    }
    if (this.client.units && !isEmpty(this.client.units)) {
      const clientsUnit = head(this.client.units);
      // Trying to find client's BU in list of clinic's BU-s
      const clientsUnitFromClinicUnitsList = find(this.unitsList, { value: clientsUnit });
      /**
       * Client stores an id of selected BU even if we delete it in Clinic's settings. Cleaning of BU for every Client
       * in the clinic will be expensive to do, so we decided to make a clean-up while editing existing Client.
       * If we cannot find already selected BU in clinic's BU-s we should update firestore with empty value
       */
      if (!clientsUnitFromClinicUnitsList) {
        await this.cleanUpClientsBusinessUnit();
      }
      this.selectedUnit = clientsUnitFromClinicUnitsList?.value ?? null;
    }

    if (this.tabToOpen) {
      this.tab = this.tabToOpen;
    }

    this.updateSuggestedCareModelCohort();
  }, // created()

  methods: {
    ...mapActions(authStore, [
      'changeCurrentClientById',
      'ensureTreatmentServicesBound',
      'ensurePlacesOfServiceBound',
    ]),
    ...mapActions(clients, ['createClient', 'updateClient']),
    ...mapActions(firebaseUploader, ['addToUploadQueue']),

    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    async onSwitchTab(tab) {
      if (tab !== 'authorizations') {
        return;
      }

      if (!this.client.id) {
        this.isAuthorizationsLoaded = true;
        return;
      }

      try {
        this.isLoadingAuthorizations = true;
        const clientAuthorizationsDb = new ClientAuthorizationsDB();
        const loadedClientAuthorizations = await clientAuthorizationsDb.readAll([
          ['clinic', '==', this.userDetails.clinic],
          ['client', '==', this.client.id],
          ['isDeleted', '==', false],
        ]);

        const localClientAuthorizations = this.editAuthorizations.filter((authorization) => authorization.editStatus);

        this.editAuthorizations = uniqBy(
          [...localClientAuthorizations, ...loadedClientAuthorizations],
          'id',
        );
      } catch (error) {
        this.notifyNegative(this.$t('common.errors.generic'));
        logEvent(error);
      } finally {
        this.isLoadingAuthorizations = false;
        this.isAuthorizationsLoaded = true;
      }
    }, // onSwitchTab()

    onRevenueCycleError(error) {
      this.notifyNegative(this.$t('common.errors.generic'));
      logEvent(error);
    }, // onRevenueCycleError()

    onCreateAuthorizations(newAuthorizations) {
      this.editAuthorizations.push(
        ...newAuthorizations.map((newAuthorization) => {
          return {
            ...newAuthorization,
            id: uid(),
            editStatus: 'create',
          };
        }),
      );
    }, // onCreateAuthorization()

    onUpdateAuthorization(newAuthorization) {
      const existingAuthorization = this.editAuthorizations.find((auth) => (auth.id === newAuthorization.id));
      this.editAuthorizations = [
        ...this.editAuthorizations.filter((auth) => (auth.id !== newAuthorization.id)),
        {
          ...newAuthorization,
          editStatus: existingAuthorization?.editStatus === 'create' ? 'create' : 'update',
        },
      ];
    }, // onUpdateAuthorization()

    onDeleteAuthorization(newAuthorization) {
      const existingAuthorization = this.editAuthorizations.find((auth) => (auth.id === newAuthorization.id));
      if (existingAuthorization?.editStatus === 'create') {
        this.editAuthorizations = this.editAuthorizations.filter((auth) => (auth.id !== newAuthorization.id));
        return;
      }

      this.editAuthorizations = [
        ...this.editAuthorizations.filter((auth) => (auth.id !== newAuthorization.id)),
        {
          ...newAuthorization,
          editStatus: 'delete',
        },
      ];
    }, // onDeleteAuthorization()

    async onRemoveValidTreatmentService(service) {
      const conflictingAppointments = await this.getConflictingAppointments(service);

      if (!conflictingAppointments.length) {
        return this.removeValidTreatmentService(service);
      }

      const treatmentService = `${service.serviceCode || ''} ${service.serviceName || ''}`;

      this.openComponentDialog(
        ServiceAppointmentConflictDialog,
        {
          treatmentService,
          appointments: conflictingAppointments,
          mode: 'delete',
        },
        async (appointmentsAction) => {
          await this.handleConflictingAppointments(appointmentsAction, service);
          this.removeValidTreatmentService(service);
        },
      );
    }, // onRemoveValidTreatmentService()

    removeValidTreatmentService(service) {
      this.confirmDialog(this.$t('common.dialogTitleConfirm'), this.$t('common.notifyDeleteConfirm'), () => {
        const existingTreatmentServicesIds = this.treatmentServices.map(({ id }) => id);

        // Remove the specified service and also clean up any services that have been deleted from the clinic
        this.editClient.validTreatmentServices = this.editClient.validTreatmentServices
          .filter((s) => s.id !== service.id && existingTreatmentServicesIds.includes(s.id));
      });
    }, // removeValidTreatmentService()

    async getConflictingAppointments(service) {
      if (!this.client.id) {
        return [];
      }

      try {
        this.isAppointmentsLoading = true;
        const appointmentsDb = new AppointmentsDB();
        const appointmentsUsingUnauthorizedService = await appointmentsDb.readAll(
          [
            ['clinic', '==', this.connectedClinic.id],
            ['client', '==', this.client.id],
            ['authorization', '==', null],
            ['treatmentService', '==', service.id],
            ['status', 'in', ['created', 'draft', 'scheduled']],
          ],
        );

        if (!appointmentsUsingUnauthorizedService.length) {
          return [];
        }

        return appointmentsUsingUnauthorizedService;
      } catch (err) {
        this.notifyNegative(err.message);
        console.error(err.message);
        logEvent(err);
      } finally {
        this.isAppointmentsLoading = false;
      }
    }, // getConflictingAppointments()

    async handleConflictingAppointments(action, service) {
      try {
        this.isAppointmentsLoading = true;
        await callFirebaseFunction('handleConflictingAppointments', {
          action,
          clinicId: this.connectedClinic.id,
          clientId: this.client.id,
          treatmentService: service.id,
        });
        this.notifySuccess(this.$t('authorizations.editAuthorization.notifyUpdateAppointmentsSuccess'));
        this.$emit('appointments-changed');
      } catch (err) {
        this.notifyNegative(err.message);
        console.error(err.message);
        logEvent(err);
      } finally {
        this.isAppointmentsLoading = false;
      }
    }, // handleConflictingAppointments()

    async onSaveClient() {
      /* @todo: When editing the clients country from 'country with states'
       to 'country without states' make sure that the state field will be null */
      // save value as array, so we can have multiple units in the future

      if (!this.editClient.clientName) {
        this.notifyNegative(this.$t('common.validateFieldFailed'));
        this.isTabChangeFromValidation = this.tab !== EDIT_CLIENT_PROFILE_TABS.PROFILE;
        this.tab = EDIT_CLIENT_PROFILE_TABS.PROFILE;
        return;
      }

      if (this.$refs.availabilityEditor?.hasErrors) {
        this.notifyNegative(this.$t('availabilityEditor.notifyConflict'));
        return;
      }

      this.editClient.units = this.selectedUnit ? [this.selectedUnit] : [];

      await this.saveClient(this.editClient);
    },

    async onCopyClientId() {
      if (this.$q.platform.is.capacitor) {
        await Clipboard.write({ string: this.client.id });
      } else {
        await copyToClipboard(this.client.id);
      }

      this.notifySuccess(this.$t('common.notifyCopiedToClipboard'));
    },

    onCopySupportInfo() {
      copySupportInfoToClipboard({ 'target client': this.client?.id });
    },

    onCancelClick() {
      // we just need to hide dialog
      this.hide();
    },

    onChangeClientStatus(newValue) {
      const shouldFireDisableClientDialog = newValue === CLIENT_STATUS.INACTIVE
        && this.client.status === CLIENT_STATUS.ACTIVE;

      if (shouldFireDisableClientDialog && this.client.id) {
        this.onDisableClient(this.client.clientName);
        return;
      }

      this.editClient.status = newValue;
    }, // onChangeClientStatus()

    onDisableClient(clientName) {
      this.openComponentDialog(
        DeleteDisableClient,
        {
          title: this.$t('clients.dialogDisableClientTitle'),
          message: this.$t('clients.dialogDisableClientMessage', {
            clientName,
          }),
          additionalText: this.$t('clients.dialogDisableClientAddMessage'),
          bannerMessage: this.$t('clients.dialogDisableClientBannerMessage'),
          bannerLinkMessage: this.$t('clients.dialogDisableClientBannerLinkMessage'),
          okLabel: this.$t('common.buttonInactivate'),
        },
        () => {
          this.editClient.status = CLIENT_STATUS.INACTIVE;
        },
      );
    }, // onDisableClient()

    mapReferenceToClientData(referenceData) {
      const syncedFields = getIntegrationManagedFields(this.connectedClinic.integration);
      const filteredFields = Object.fromEntries(syncedFields.map((fieldName) => {
        return [fieldName, referenceData?.[fieldName] || null];
      }));

      const {
        units: refUnits,
        birthDate,
        country: referenceCountry,
        state,
        gender,
        ...rest
      } = filteredFields;

      let existingUnit = null;

      if (refUnits?.length > 0) {
        const firstExistingRefUnit = refUnits.find((refUnitName) => {
          return this.units.some(({ unitName }) => refUnitName === unitName);
        });

        if (firstExistingRefUnit) {
          existingUnit = this.units.find(({ unitName }) => unitName === firstExistingRefUnit);
        }
      }

      let shiftedBirthDate = null;

      if (birthDate) {
        // We set birthDate to 12:00 PM to avoid timezone issues
        const convertedBirthDate = birthDate instanceof Timestamp ? birthDate.toDate() : new Date(birthDate);
        shiftedBirthDate = Timestamp.fromDate(this.addToDate(convertedBirthDate, { hours: 12 }));
      }

      let genderToWrite = null;

      if (gender) {
        switch (gender.toLowerCase()) {
          case 'male':
          case 'm':
            genderToWrite = 'male';
            break;
          case 'female':
          case 'f':
            genderToWrite = 'female';
            break;
          default:
            genderToWrite = 'other';
        }
      }

      let countryToWrite = null;

      if (countries.isValid(referenceCountry)) {
        countryToWrite = countries.toAlpha2(referenceCountry);
      } else {
        countryToWrite = countries.getAlpha2Code(referenceCountry, 'en')
          || countries.getSimpleAlpha2Code(referenceCountry, 'en')
          || this.editClient.country;
      }

      const stateToWrite = this.countryStates(countryToWrite)?.find(({ code, label }) => {
        const uppercaseState = state?.toUpperCase() || '';
        return code.toUpperCase() === uppercaseState || label.toUpperCase() === uppercaseState;
      })?.code || null;

      return {
        ...rest,
        birthDate: shiftedBirthDate,
        units: existingUnit ? [existingUnit.id] : [],
        gender: genderToWrite,
        ...(countryToWrite && { country: countryToWrite }),
        state: stateToWrite,
      };
    },

    onEditIntegration() {
      this.openComponentDialog(
        EditIntegration,
        {
          integrationReference: get(this.editClient, 'integrationReference'),
          savedIntegrationReference: get(this.client, 'integrationReference'),
          objectType: 'client',
          clientId: this.client.id,
        },
        async (integrationClientInfo) => {
          if (integrationClientInfo === null) {
            this.editClient.integrationReference = null;
          } else {
            this.integrationLinkEdited = true;

            let clientProfileFromIntegration = {};

            if (this.integrationSettings.isIntegrationSourceOfTruth) {
              const profileData = this.connectedClinic.integration === INTEGRATION_NAMES.LUMARY
                ? integrationClientInfo : integrationClientInfo.profile;
              const { units: clientUnits, ...rest } = this.mapReferenceToClientData(profileData);
              clientProfileFromIntegration = rest;
              this.selectedUnit = clientUnits[0] || null;
            }

            this.editClient = {
              ...this.editClient,
              ...clientProfileFromIntegration,
              integrationReference: integrationClientInfo.refId || null,
              clientName: this.editClient.clientName || integrationClientInfo.fullName || '',
            };
          }
        },
      );
    }, // onEditIntegration()

    /**
     * Corrections made to the client from the coverage tab's policyholder form.
     *
     * Merged into the draft rather than saved, so the client still has one writer and these fields
     * commit with everything else on this form. The date arrives as a plain Date at noon, which is
     * the same convention the details tab writes with; the storage type is ours to apply.
     */
    onCoveragePolicyholderEdited(patch) {
      this.editClient = {
        ...this.editClient,
        ...patch,
        birthDate: patch.birthDate === null ? null : Timestamp.fromDate(patch.birthDate),
      };
    }, // onCoveragePolicyholderEdited()

    onAddDiagnosis() {
      this.openComponentDialog(
        EditDiagnosisDialog,
        { diagnosis: {} },
        (diagnosis) => this.upsertDiagnosis(diagnosis),
      );
    }, // onAddDiagnosis()

    onEditDiagnosis(diagnosis) {
      this.openComponentDialog(
        EditDiagnosisDialog,
        { diagnosis },
        (updated) => this.upsertDiagnosis(updated),
      );
    }, // onEditDiagnosis()

    upsertDiagnosis(diagnosis) {
      const diagnoses = [...(this.editClient.diagnoses || [])];
      if (diagnosis.id) {
        const index = diagnoses.findIndex((entry) => entry.id === diagnosis.id);
        if (index !== -1) {
          diagnoses.splice(index, 1, diagnosis);
        }
      } else {
        const nextOrder = diagnoses.reduce((max, entry) => Math.max(max, entry.order ?? -1), -1) + 1;
        diagnoses.push({
          ...diagnosis,
          id: uid(),
          order: nextOrder,
          // Effective-dated fields default to unknown/active; entry UI arrives with the ICD-10 picker.
          diagnosedDate: diagnosis.diagnosedDate ?? null,
          endDate: diagnosis.endDate ?? null,
        });
      }
      // Reassign so the deep `editClient` watcher (and save-on-submit) picks up the change.
      this.editClient.diagnoses = diagnoses;
    }, // upsertDiagnosis()

    updateDiagnosis(id, patch) {
      this.editClient.diagnoses = (this.editClient.diagnoses || []).map(
        (entry) => (entry.id === id ? { ...entry, ...patch } : entry),
      );
    }, // updateDiagnosis()

    resolveDiagnosis(diagnosis) {
      // Resolve as of now; editing the exact date arrives with the DS date field.
      // Drop `order`: resolved diagnoses leave the priority list entirely (sorted by endDate).
      this.updateDiagnosis(diagnosis.id, { endDate: Timestamp.now(), order: null });
    }, // resolveDiagnosis()

    reactivateDiagnosis(diagnosis) {
      // Re-enter at the bottom of the active priority list rather than restoring the
      // old order, so reactivating never silently reclaims the primary slot.
      const nextOrder = this.activeDiagnoses.reduce((max, entry) => Math.max(max, entry.order ?? -1), -1) + 1;
      this.updateDiagnosis(diagnosis.id, { endDate: null, order: nextOrder });
    }, // reactivateDiagnosis()

    deleteDiagnosis(diagnosis) {
      confirmDestructiveDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t('clients.modals.editClient.clinical.deleteConfirm', { code: diagnosis.code }),
        () => {
          this.editClient.diagnoses = (this.editClient.diagnoses || []).filter(
            (entry) => entry.id !== diagnosis.id,
          );
        },
        { confirmLabel: this.$t('common.buttonDelete') },
      );
    }, // deleteDiagnosis()

    onReorderActiveDiagnoses(reorderedActive) {
      // HrSortableList emits the active list in its new order after a drag.
      this.persistDiagnosesOrder(reorderedActive);
    }, // onReorderActiveDiagnoses()

    persistDiagnosesOrder(newActiveOrder) {
      // Renumber only the active list (0-based priority, index 0 = primary). Resolved diagnoses
      // stay out of the priority order and keep `order` null (normalizing any legacy values).
      const active = newActiveOrder.map((diagnosis, index) => ({ ...diagnosis, order: index }));
      const resolved = this.resolvedDiagnoses.map((diagnosis) => ({ ...diagnosis, order: null }));
      this.editClient.diagnoses = [...active, ...resolved];
    }, // persistDiagnosesOrder()

    diagnosisMenuOptions(diagnosis) {
      const editOption = {
        label: this.$t('common.buttonEdit'),
        icon: 'edit',
        action: () => this.onEditDiagnosis(diagnosis),
      };
      const deleteGroup = {
        group: 'destructive',
        options: [
          {
            label: this.$t('common.buttonDelete'),
            icon: 'delete',
            destructive: true,
            action: () => this.deleteDiagnosis(diagnosis),
          },
        ],
      };

      const resolutionOption = diagnosis.endDate
        ? {
          label: this.$t('clients.modals.editClient.clinical.reactivate'),
          icon: 'restart_alt',
          action: () => this.reactivateDiagnosis(diagnosis),
        }
        : {
          label: this.$t('clients.modals.editClient.clinical.resolve'),
          icon: 'check_circle',
          action: () => this.resolveDiagnosis(diagnosis),
        };

      return [
        {
          group: 'lifecycle',
          options: [
            editOption,
            resolutionOption,
          ],
        },
        deleteGroup,
      ];
    }, // diagnosisMenuOptions()

    async saveClient(client) {
      const integrationClientUpdated = this.connectedClinic.integration
        && (get(this.client, 'integrationReference', null) !== get(client, 'integrationReference', null));

      let updatedClient = null;
      try {
        this.waitingSpinner = true;

        let successMessage = '';

        const saveClient = {
          ...client,
          avatarInitials: this.getClientInitials(client.clientName),
        };
        if (this.isAuthorizationsLoaded) {
          // For convinience, we are saving whether or not the client has authorizations
          // in the client object - this way we don't need to try to fetch the authorizations
          // during shceduling if the client has none. We only overwrite the value if
          // we have loaded the authorizations for the client
          saveClient.hasAuthorizations = this.editAuthorizations
            .filter((auth) => (auth.editStatus !== 'delete'))
            .length > 0;
        }

        if (!saveClient.id) {
          if (!this.userHasPrivilege('clients/addClients')) {
            return;
          }

          // Add random avatar background color
          if (!saveClient.avatarBgColor) {
            saveClient.avatarBgColor = this.getRandomColor();
          }

          updatedClient = await this.createClient(saveClient);
          await this.saveClientPhoto(updatedClient.id);

          if (updatedClient.id && isEmpty(this.currentClient)) {
            this.changeCurrentClientById(updatedClient.id);
          }

          successMessage = this.$t('clients.notifyAddClientSuccess', { clientName: saveClient.clientName });
        } else {
          // Update an existing client
          if (saveClient.avatarBgColor === undefined || saveClient.avatarBgColor === null
          ) {
            saveClient.avatarBgColor = this.getRandomColor();
          }

          const promises = [
            this.updateClient(saveClient),
            this.saveClientPhoto(saveClient.id),
          ];

          if (this.deleteClientPhoto && this.client.photo) {
            const clientPhotoRef = ref(storage, this.client.photo);

            if (clientPhotoRef) {
              promises.push(deleteObject(clientPhotoRef));
            }
          }
          // Update an existing client (client.id is empty)
          await Promise.all(promises);
          updatedClient = saveClient;

          this.createClientsTriggerNotifications(this.client.id, CLIENTS_NOTIFICATIONS_TYPES.CLIENT_UPDATED);
          successMessage = this.$t('clients.notifyUpdateClientSuccess', { clientName: saveClient.clientName });
        }

        this.updateLicenseUsage(this.licenseNeeded);

        const updateActions = [];
        updateActions.push(this.updateClientInExistingAppointments(saveClient, updatedClient.id));
        updateActions.push(this.updateClientUsersBinding(updatedClient));
        updateActions.push(this.updateClientAuthorizations(updatedClient.id, this.editAuthorizations));
        // Coverage stages its edits and flushes here, once the client id exists — which is what
        // lets coverage be recorded while creating a client.
        // The CLIENT's clinic, not the writer's — a role-less user linked across clinics can
        // reach this client, and stamping their clinic would orphan the coverage from the clinic
        // that owns them. The Firestore rule enforces the same thing.
        updateActions.push(this.clientCoverages.commit({
          clientId: updatedClient.id,
          clinicId: saveClient.clinic,
        }));
        // Same contract as coverage above, including the CLIENT's clinic rather than the writer's.
        updateActions.push(this.clientAuthorizations.commit({
          clientId: updatedClient.id,
          clinicId: saveClient.clinic,
        }));

        await Promise.all(updateActions);

        if (
          POLLING_DRIVEN_INTEGRATIONS.includes(this.connectedClinic.integration)
          && integrationClientUpdated
          && !!saveClient.integrationReference
        ) {
          this.refreshIntegrationAppointments(saveClient.integrationReference);
        }

        if (
          V2_DRIVEN_INTEGRATIONS.includes(this.connectedClinic.integration)
          && integrationClientUpdated
          && this.integrationLinkEdited
          && !!saveClient.integrationReference
        ) {
          callFirebaseFunction('backfillV2AppointmentsForRef', {
            clinicId: this.connectedClinic.id,
            refId: saveClient.integrationReference,
            objectType: 'client',
          }).catch((err) => console.error('backfillV2AppointmentsForRef failed', err));
        }

        if (
          !POLLING_DRIVEN_INTEGRATIONS.includes(this.connectedClinic.integration)
          && integrationClientUpdated
          && this.integrationLinkEdited
        ) {
          await callFirebaseFunction('mapSessionWithAppointments', {
            clientId: updatedClient.id,
          });
        }

        this.notifySuccess(successMessage);

        const wasDeactivatedOnSave = !!this.client.id && this.client.status === CLIENT_STATUS.ACTIVE
          && saveClient.status === CLIENT_STATUS.INACTIVE;

        if (wasDeactivatedOnSave) {
          this.notifySuccess(this.$t('clients.notifyDisableClientSuccess', { clientName: saveClient.clientName }));
        }
      } catch (error) {
        logEvent(error);
        console.error('Error updating client', error);
        const isOfflineError = ['internal', 'functions/internal'].includes(error.code)
          && error.message === 'internal'
          && !this.networkOnline;
        const errorMessage = isOfflineError ? this.$t('common.noInternetConnectionMessage') : error.message;
        notifyErrorWithDetails(errorMessage, error.details?.fullMessage);
      } finally {
        this.waitingSpinner = false;
      }
      // on OK, it is REQUIRED to
      // emit "ok" event (with optional payload)
      // before hiding the QDialog
      this.$emit('ok', updatedClient);
      // or with payload: this.$emit('ok', { ... })

      // then hiding dialog
      this.hide();
    }, // saveClient()

    onSelectUploadedFile(evt) {
      const file = head(evt.target.files || evt.dataTransfer.files);

      if (!file) {
        return;
      }

      const extension = chain(file)
        .get('name')
        .split('.')
        .last()
        .value();

      if (!extension) {
        return;
      }

      this.selectedClientPhoto = file;
      this.deleteClientPhoto = false;

      if (this.$refs.file) {
        this.$refs.file.value = '';
      }
    }, // onSelectUploadedFile()

    saveClientPhoto(clientId) {
      if (!this.selectedClientPhoto || !clientId) {
        return;
      }

      const extension = chain(this.selectedClientPhoto)
        .get('name')
        .split('.')
        .last()
        .value();

      // Save client photo
      const fileInfo = {
        destinationPath: `/clients/${this.connectedClinic.id}/${clientId}/avatar/`,
        destinationFilename: `photo.${extension}`,
        file: this.selectedClientPhoto,
        metadata: {
          customMetadata: {
            clientPhoto: true,
          },
        },
        callback: async (fileRef) => {
          const clientInfo = {
            id: clientId,
            photo: await getDownloadURL(fileRef),
          };

          this.updateClient(clientInfo);
        },
      };

      this.addToUploadQueue(fileInfo);
    }, // saveClientPhoto()

    onPhotoDelete() {
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('clients.modals.editClient.dialogDeleteClientPhoto'),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          this.editClient.photo = null;
          this.selectedClientPhoto = null;

          if (this.client.photo) {
            this.deleteClientPhoto = true;
            this.editClient.avatarBgColor = this.getRandomColor();
          }
        });
    }, // onPhotoDelete()

    refreshIntegrationAppointments(integrationReference) {
      callFirebaseFunction('refreshAppointments', {
        clinicId: this.userDetails.clinic,
        objectType: 'client',
        objectId: integrationReference,
        ignoreModifiedSince: true,
      }, {
        timeout: 540000, // timeout aligned with firebase function timeout
      });
    },

    async updateExistingAppointment(clientId, oldIntegrationRef, newIntegrationRef) {
      await updateAppointmentClientReference(
        this.connectedClinic.id,
        newIntegrationRef ? clientId : null,
        oldIntegrationRef || newIntegrationRef,
      );

      const eventType = newIntegrationRef === null ? 'PatientMappingsRemoved' : 'PatientMappingsAdded';
      await sendEventToAloha(this.connectedClinic.id, eventType, { refId: newIntegrationRef || oldIntegrationRef });
    }, // updateExistingAppointment()

    async updateClientInExistingAppointments(clientData, clientId) {
      const oldIntegrationRef = get(this.client, 'integrationReference', null);
      const newIntegrationRef = get(clientData, 'integrationReference', null);

      if (this.connectedClinic.integration === 'aloha' && (oldIntegrationRef !== newIntegrationRef)) {
        // when reference is switched from one aloha profile to another, we need to update apts for two profiles
        if (oldIntegrationRef && newIntegrationRef) {
          await Promise.all([
            this.updateExistingAppointment(clientId, null, newIntegrationRef),
            this.updateExistingAppointment(clientId, oldIntegrationRef, null),
          ]);
        } else {
          await this.updateExistingAppointment(clientId, oldIntegrationRef, newIntegrationRef);
        }
      }
    }, // updateClientInExistingAppointments()

    updateClientAuthorizations(clientId, authorizations) {
      const clientAuthorizationsDb = new ClientAuthorizationsDB();
      const updateActions = authorizations
        .filter((auth) => (['create', 'update', 'delete'].includes(auth.editStatus)))
        .map((auth) => {
          switch (auth.editStatus) {
            case 'create':
              return clientAuthorizationsDb.create({
                ...omit(auth, ['id', 'editStatus']),
                isDeleted: false,
                client: clientId,
                clinic: this.userDetails.clinic,
              });
            case 'update':
              return clientAuthorizationsDb.set({
                ...omit(auth, ['id', 'editStatus']),
              }, auth.id);
            case 'delete':
              return clientAuthorizationsDb.set({
                ...omit(auth, ['id', 'editStatus']),
                isDeleted: true,
              }, auth.id);
            default:
              return null;
          }
        });

      return Promise.all(updateActions);
    }, // updateClientAuthorizations()

    isUserBoundWithUnit(user) {
      return this.roleHasPrivilege(
        this.cachedUserPrivileges,
        user.userPrivilege || user.role,
        'clients/accessAllClientsInOwnUnits',
      ) && !!find(this.client.units, (clientUnitId) => includes(get(user, 'units'), clientUnitId));
    }, // isUserBoundWithUnit()

    maxBirthDate(d) {
      return d <= this.formatDate(new Date(), 'yyyy/MM/dd');
    }, // maxBirthDate()

    getDataFieldOptions(dataField) {
      return chain(dataField)
        .get('options')
        .map((option) => ({ value: option, label: option }))
        .value();
    }, // getDataFieldOptions()

    getClientData(dataField) {
      let defaultValue = null;
      switch (dataField.type) {
        case 'yesNo': {
          defaultValue = null;
          break;
        }
        case 'select': {
          if (dataField.allowMultipleOptions) {
            defaultValue = [];
          } else {
            defaultValue = '';
          }
          break;
        }
        default:
      }
      return get(this.editClient, ['customData', dataField.id], defaultValue);
    }, // getClientData()

    setClientData(dataFieldId, value) {
      this.editClient.customData = {
        ...this.editClient.customData,
        [dataFieldId]: value !== undefined ? value : null,
      };
    }, // setClientData()

    onClientDataSectionShown(section) {
      if (section?.id !== this.clientSchedulingGroupsSection?.id) {
        return;
      }

      editClientProfileAnalytics.trackProfileSchedulingGroupsExpanded({
        targetClientId: this.client.id,
        source: this.source,
      });
    }, // onClientDataSectionShown()

    getFullName(firstName, lastName) {
      return chain([firstName, lastName])
        .map((name) => trim(name))
        .compact()
        .join(' ')
        .value();
    },

    async cleanUpClientsBusinessUnit() {
      if (this.client?.id) {
        await this.updateClient({
          id: this.client.id,
          units: [],
        });
      }
    }, // cleanUpClientsBusinessUnit()

    integrationSourceOfTruthText(type = 'integrationIsSourceOfTruthTooltip') {
      return this.$t(
        `clients.modals.editClient.${type}`,
        { integration: HUMANIZED_INTEGRATION_NAMES[this.connectedClinic.integration] },
      );
    },

    updateSuggestedCareModelCohort() {
      if (!this.hasCareModelCohorts) {
        this.suggestedCareModelCohortId = null;

        if (!this.client?.id && !this.isCareModelCohortManuallyChanged) {
          this.editClient.careModelCohort = null;
        }
        return;
      }

      const match = this.getSuggestedCohortForClient({
        client: this.editClient,
        cohorts: this.careModelCohorts,
      });

      this.suggestedCareModelCohortId = match?.cohort?.id || null;

      if (!this.client?.id && !this.isCareModelCohortManuallyChanged) {
        this.editClient.careModelCohort = this.suggestedCareModelCohortId;
      }
    },

    onChangeCareModelCohort(value) {
      this.editClient.careModelCohort = value || null;
      this.isCareModelCohortManuallyChanged = true;
    },

    isSuggestedCareModelCohort(cohortId) {
      return !!cohortId
        && !!this.suggestedCareModelCohortId
        && cohortId === this.suggestedCareModelCohortId;
    },

    getCareModelCohortOptionCaption(option) {
      if (!this.isMobileScreen || !this.isSuggestedCareModelCohort(option?.id)) {
        return option?.description || '';
      }

      if (option?.description) {
        return `${option.description} • ${this.$t('cohorts.suggested')}`;
      }

      return this.$t('cohorts.suggested');
    },

    isIntegrationManaged(fieldName) {
      return this.isIntegrationSourceOfTruth
        && INTEGRATIONS_CLIENT_PROFILE_FIELDS.includes(fieldName)
        && !EXCLUDE_CLIENT_PROFILE_FIELDS_SYNC[this.connectedClinic.integration]?.includes(fieldName);
    },

    onChangeSelectedUnit(value) {
      this.editClient.units = value ? [value] : [];
      this.updateSuggestedCareModelCohort();
    },
  },
};
</script>

<style lang="scss" scoped>

/* Diagnosis rows are interacted with (tap to edit, drag to reorder), not read as selectable text. */
.diagnoses {
  user-select: none;
  -webkit-user-select: none;
}

/* Separate the historical group from the live one. The de-emphasis itself is semantic — the rows
   carry the `muted` prop — so no color tokens leak into this layer. */
.diagnoses-resolved {
  margin-top: 24px;
}

.avatar-actions {
  position: absolute;
  top: -8px;
  right: -20px;
  z-index: 10;
}

.client-photo-overlay {
  background-color: rgba(0, 0, 0, .5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 1;
  }
}

.cohort-option-label {
  white-space: normal;
  word-break: break-word;
  line-height: 1.3;
}

.cohort-select :deep(.q-field__native),
.cohort-select :deep(.q-field__input) {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  line-height: 1.3;
}

.cohort-select :deep(.q-field__control) {
  min-height: 56px;
  height: auto;
}

.cohort-select :deep(.q-field__marginal) {
  align-self: flex-start;
  padding-top: 8px;
}
</style>
